import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getCounterpart } from "@/lib/routes";
import { getPostByAnySlug } from "@/content/blog";

type AppLanguage = "de" | "en";

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Resolve counterpart URLs upfront so each option renders as a real
  // crawlable <a href="..."> via React Router <Link>. For blog post pages
  // we look up the post's localized slug map so the switch links straight
  // to /<locale>/blog/{localized-slug} — no in-component redirect, no
  // double mount, no lag. Falls back to the locale home for non-mapped
  // routes (e.g. /admin), guaranteeing a valid href for SEO discovery.
  const blogSlugMap = (() => {
    const m = location.pathname.match(/^\/(?:de|en)\/blog\/(.+)$/);
    if (!m) return undefined;
    const found = getPostByAnySlug(m[1]);
    return found ? found.post.slug : undefined;
  })();

  const deHref = getCounterpart(location.pathname, "de", blogSlugMap) ?? "/de";
  const enHref = getCounterpart(location.pathname, "en", blogSlugMap) ?? "/en";

  const handleSelect = useCallback(
    (nextLanguage: AppLanguage) => {
      setOpen(false);
      // Keep Header/Footer labels in sync during the brief navigation frame.
      // LocaleRoute on the destination will reconcile context to the URL.
      if (nextLanguage !== language) setLanguage(nextLanguage);
    },
    [language, setLanguage],
  );

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="w-12 sm:w-16 shrink-0 justify-center gap-1 sm:gap-2 px-2">
          <Globe className="hidden sm:inline-block h-4 w-4" />
          <span className="uppercase" data-nosnippet="">{language}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          asChild
          className={language === "de" ? "bg-primary/10 text-primary" : ""}
        >
          <Link
            to={deHref}
            hrefLang="de"
            state={{ preserveScrollY: typeof window !== "undefined" ? window.scrollY : 0 }}
            onClick={() => handleSelect("de")}
          >
            Deutsch
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className={language === "en" ? "bg-primary/10 text-primary" : ""}
        >
          <Link
            to={enHref}
            hrefLang="en"
            state={{ preserveScrollY: typeof window !== "undefined" ? window.scrollY : 0 }}
            onClick={() => handleSelect("en")}
          >
            English
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

